import React from 'react';

import LinkButton from '@atlaskit/button/link';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Box } from '@atlaskit/primitives/compiled/box';

import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { PAGE_ANALYTICS } from '@confluence/named-routes/entry-points/routes.ts';

const styles = cssMap({
	container: { width: '100%' },
	anchor: {
		width: '100%',
		textAlign: 'center',
		textDecoration: 'none',
		'&:hover': { textDecoration: 'none' },
	},
	button: {
		width: '100%',
		height: '32px',
		borderRadius: token('radius.xsmall'),
		textDecoration: 'none',
		backgroundColor: token('color.background.neutral'),
		color: token('color.text'),
		fontWeight: token('font.weight.medium'),
		'&:hover': { backgroundColor: token('color.background.neutral.hovered') },
		'&:active': { backgroundColor: token('color.background.neutral.pressed') },
	},
});

export type ContentAnalyticsLinkProps = React.PropsWithChildren<{
	isCompactLayout?: boolean;
	contentId?: string;
	contentTab?: 'Overview' | 'Mentions' | 'Links';
	testId?: string;
	onClick?: () => void;
}>;

export const ContentAnalyticsLink = ({
	isCompactLayout = false,
	contentId,
	contentTab,
	onClick,
	testId,
	children,
}: ContentAnalyticsLinkProps): React.JSX.Element | null => {
	const spaceKey = useSpaceKey() || ``;

	// spaceKey and contentId are required for PAGE_ANALYTICS.toUrl
	if (!spaceKey || !contentId) {
		return null;
	}

	const pageAnalyticsURL = PAGE_ANALYTICS.toUrl({
		spaceKey,
		contentId,
		tab: contentTab ? contentTab.toLowerCase() : undefined,
	});

	if (isCompactLayout) {
		return (
			<Box xcss={styles.container} paddingInline="space.150">
				<LinkButton href={pageAnalyticsURL} onClick={onClick} testId={testId} shouldFitContainer>
					{children}
				</LinkButton>
			</Box>
		);
	}

	return (
		<Box paddingBlockStart="space.200" xcss={styles.container}>
			<Anchor href={pageAnalyticsURL} onClick={onClick} testId={testId} xcss={styles.anchor}>
				<Inline xcss={styles.button} alignInline="center" alignBlock="center">
					{children}
				</Inline>
			</Anchor>
		</Box>
	);
};
